class MegaMenuLocalization extends HTMLElement {
  connectedCallback() {
    this.onChange = this.handleChange.bind(this);
    this.querySelectorAll('select').forEach((select) => {
      this.syncSelectSizer(select);
      select.addEventListener('change', this.onChange);
    });

    if (document.fonts?.ready) {
      document.fonts.ready.then(() => {
        this.querySelectorAll('select').forEach((select) => this.syncSelectSizer(select));
      });
    }
  }

  syncSelectSizer(select) {
    const sizer = select.parentElement?.querySelector(
      '.mega-menu-footer__select-sizer, .footer-localization__select-sizer'
    );
    if (!sizer) return;

    sizer.textContent = select.options[select.selectedIndex]?.textContent.trim() || '';

    // Native <select> uses the longest option for intrinsic width; size to the sizer instead.
    const nextWidth = sizer.scrollWidth;
    if (nextWidth > 0) {
      select.style.minWidth = '0';
      select.style.width = `${nextWidth + 2}px`;
    }
  }

  disconnectedCallback() {
    this.querySelectorAll('select').forEach((select) => {
      select.removeEventListener('change', this.onChange);
    });
  }

  handleChange(event) {
    const select = event.target;
    const form = select.closest('form');
    if (!form) return;

    this.syncSelectSizer(select);
    form.submit();
  }
}

if (!customElements.get('mega-menu-localization')) {
  customElements.define('mega-menu-localization', MegaMenuLocalization);
}

class HeaderMegaMenu extends HTMLElement {
  connectedCallback() {
    this.header = document.querySelector('.header-wrapper');
    this.content = this.querySelector('.mega-menu__content');
    this.surface = this.querySelector('.mega-menu__surface');
    this.closeButton = this.querySelector('.mega-menu__close');
    this.triggers = [...this.querySelectorAll('.mega-menu__trigger')];
    this.panels = [...this.querySelectorAll('.mega-menu__panel')];

    if (!this.triggers.length || !this.content) return;

    this.isOpen = false;
    this.activeTrigger = null;

    this.onTriggerClick = this.handleTriggerClick.bind(this);
    this.onCloseClick = this.handleCloseClick.bind(this);
    this.onFocusOut = this.handleFocusOut.bind(this);
    this.onKeyDown = this.handleKeyDown.bind(this);
    this.onTransitionEnd = this.handleTransitionEnd.bind(this);

    this.triggers.forEach((trigger) => {
      trigger.addEventListener('click', this.onTriggerClick);
    });
    this.closeButton?.addEventListener('click', this.onCloseClick);
    this.addEventListener('focusout', this.onFocusOut);
    this.addEventListener('keydown', this.onKeyDown);
    this.content.addEventListener('transitionend', this.onTransitionEnd);
    this.onResize = this.syncOpenWidth.bind(this);
    window.addEventListener('resize', this.onResize);

    this.syncOpenWidth();
    this.initFlyoutSubmenus();
  }

  disconnectedCallback() {
    if (!this.triggers.length || !this.content) return;

    this.triggers.forEach((trigger) => {
      trigger.removeEventListener('click', this.onTriggerClick);
    });
    this.closeButton?.removeEventListener('click', this.onCloseClick);
    this.removeEventListener('focusout', this.onFocusOut);
    this.removeEventListener('keydown', this.onKeyDown);
    this.content.removeEventListener('transitionend', this.onTransitionEnd);
    window.removeEventListener('resize', this.onResize);
    this.destroyFlyoutSubmenus();

    if (this.isOpen) {
      this.unlockPageScroll();
    }
  }

  syncOpenWidth() {
    const containingBlock = this.content?.offsetParent || this.header;
    if (!containingBlock || !this.content) return;

    const openWidth = Math.round(containingBlock.getBoundingClientRect().width + 16);
    this.content.style.setProperty('--mega-menu-open-width', `${openWidth + 40}px`);
  }

  initFlyoutSubmenus() {
    this.flyoutLists = [...this.querySelectorAll('.mega-menu__list--flyout')];
    this.flyoutHandlers = [];
    this.activeSubmenu = null;
    this.activeLevel2Link = null;

    this.flyoutLists.forEach((list) => {
      const onListLeave = () => this.hideActiveSubmenu();
      list.addEventListener('mouseleave', onListLeave);
      this.flyoutHandlers.push({ element: list, type: 'mouseleave', handler: onListLeave });

      list.querySelectorAll('.mega-menu__link--level-2').forEach((link) => {
        const onLinkEnter = () => {
          const submenu = link.nextElementSibling;

          if (submenu?.classList.contains('mega-menu__list--level-3')) {
            this.showActiveSubmenu(link, submenu);
            return;
          }

          this.hideActiveSubmenu();
        };

        link.addEventListener('mouseenter', onLinkEnter);
        this.flyoutHandlers.push({ element: link, type: 'mouseenter', handler: onLinkEnter });
      });

      list.querySelectorAll('.mega-menu__list--level-3').forEach((submenu) => {
        const onSubmenuEnter = () => {
          const link = submenu.previousElementSibling;

          if (link?.classList.contains('mega-menu__link--level-2')) {
            this.showActiveSubmenu(link, submenu);
          }
        };

        submenu.addEventListener('mouseenter', onSubmenuEnter);
        this.flyoutHandlers.push({ element: submenu, type: 'mouseenter', handler: onSubmenuEnter });
      });
    });
  }

  destroyFlyoutSubmenus() {
    this.flyoutHandlers?.forEach(({ element, type, handler }) => {
      element.removeEventListener(type, handler);
    });
    this.flyoutHandlers = [];
    this.hideActiveSubmenu();
  }

  showActiveSubmenu(link, submenu) {
    if (this.activeLevel2Link === link && this.activeSubmenu === submenu) return;

    this.hideActiveSubmenu();
    this.activeLevel2Link = link;
    this.activeSubmenu = submenu;
    link.classList.add('is-hovered');
    link.setAttribute('aria-expanded', 'true');
    submenu.classList.add('is-visible');
  }

  hideActiveSubmenu() {
    if (this.activeLevel2Link) {
      this.activeLevel2Link.classList.remove('is-hovered');
      this.activeLevel2Link.setAttribute('aria-expanded', 'false');
      this.activeLevel2Link = null;
    }

    if (this.activeSubmenu) {
      this.activeSubmenu.classList.remove('is-visible');
      this.activeSubmenu = null;
    }
  }

  handleCloseClick(event) {
    event.preventDefault();
    if (!this.isOpen) return;

    const trigger = this.activeTrigger;
    this.close();
    trigger?.focus();
  }

  handleTriggerClick(event) {
    event.preventDefault();
    const trigger = event.currentTarget;
    const panelId = trigger.dataset.menuPanel;

    if (this.isOpen && this.activeTrigger === trigger) {
      this.close();
      return;
    }

    this.showPanel(panelId);
    this.setActiveTrigger(trigger);

    if (!this.isOpen) {
      this.open();
    }
  }

  showPanel(panelId) {
    this.hideActiveSubmenu();
    this.panels.forEach((panel) => {
      const isActive = panel.id === panelId;
      panel.hidden = !isActive;
      panel.classList.toggle('mega-menu__panel--active', isActive);
    });
  }

  setActiveTrigger(trigger) {
    this.triggers.forEach((item) => {
      item.setAttribute('aria-expanded', item === trigger ? 'true' : 'false');
    });
    this.activeTrigger = trigger;
  }

  lockPageScroll() {
    document.body.classList.add('overflow-hidden');
  }

  unlockPageScroll() {
    document.body.classList.remove('overflow-hidden');
  }

  open() {
    if (this.isOpen) return;

    this.syncOpenWidth();
    this.isOpen = true;
    this.content.removeAttribute('inert');
    this.content.setAttribute('aria-hidden', 'false');
    this.lockPageScroll();
    this.onToggle();

    setTimeout(() => {
      this.classList.add('is-open');
    });
  }

  close() {
    if (!this.isOpen) return;

    this.hideActiveSubmenu();
    this.isOpen = false;
    this.classList.remove('is-open');
    this.triggers.forEach((trigger) => {
      trigger.setAttribute('aria-expanded', 'false');
    });
    this.activeTrigger = null;
    this.content.setAttribute('inert', '');
    this.unlockPageScroll();
    this.onToggle();
  }

  handleTransitionEnd(event) {
    if (event.target !== this.content || event.propertyName !== 'width' || this.isOpen) return;
    this.content.setAttribute('aria-hidden', 'true');
    this.panels.forEach((panel) => {
      panel.hidden = true;
      panel.classList.remove('mega-menu__panel--active');
    });
  }

  handleFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  handleKeyDown(event) {
    if (event.code !== 'Escape' || !this.isOpen) return;
    event.preventDefault();
    const trigger = this.activeTrigger;
    this.close();
    trigger?.focus();
  }

  onToggle() {
    if (!this.header) return;

    this.header.preventHide = this.isOpen;

    if (document.documentElement.style.getPropertyValue('--header-bottom-position-desktop') !== '') return;

    document.documentElement.style.setProperty(
      '--header-bottom-position-desktop',
      `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
    );
  }
}

if (!customElements.get('header-mega-menu')) {
  customElements.define('header-mega-menu', HeaderMegaMenu);
}

class HeaderSearch extends HTMLElement {
  connectedCallback() {
    this.toggle = this.querySelector('.header__icon--search');
    this.modal = this.querySelector('.search-modal');
    this.closeButton = this.querySelector('.search-modal__close');

    if (!this.toggle || !this.modal) return;

    this.isOpen = false;
    this.onToggleClick = this.handleToggleClick.bind(this);
    this.onCloseClick = this.handleCloseClick.bind(this);
    this.onBodyClick = this.handleBodyClick.bind(this);
    this.onKeyDown = this.handleKeyDown.bind(this);

    this.toggle.addEventListener('click', this.onToggleClick);
    this.closeButton?.addEventListener('click', this.onCloseClick);
    this.addEventListener('keydown', this.onKeyDown);
  }

  disconnectedCallback() {
    this.toggle?.removeEventListener('click', this.onToggleClick);
    this.closeButton?.removeEventListener('click', this.onCloseClick);
    this.removeEventListener('keydown', this.onKeyDown);
    document.body.removeEventListener('click', this.onBodyClick);

    if (this.isOpen) {
      document.body.classList.remove('overflow-hidden');
    }
  }

  handleToggleClick(event) {
    event.preventDefault();
    this.isOpen ? this.close() : this.open();
  }

  handleCloseClick(event) {
    event.preventDefault();
    this.close();
  }

  handleBodyClick(event) {
    if (!this.contains(event.target)) this.close(false);
  }

  handleKeyDown(event) {
    if (event.code !== 'Escape' || !this.isOpen) return;
    event.preventDefault();
    this.close();
  }

  open() {
    if (this.isOpen) return;

    this.isOpen = true;
    this.modal.classList.remove('hidden');
    this.toggle?.setAttribute('aria-expanded', 'true');
    this.modal.removeAttribute('inert');
    this.modal.setAttribute('aria-hidden', 'false');
    document.body.addEventListener('click', this.onBodyClick);
    document.body.classList.add('overflow-hidden');

    // Defer class so the width transition always runs
    setTimeout(() => {
      this.classList.add('is-open');
    });

    trapFocus(
      this.modal.querySelector('[tabindex="-1"]'),
      this.querySelector('.search-modal__input')
    );
  }

  close(focusToggle = true) {
    if (!this.isOpen) return;

    this.isOpen = false;
    this.classList.remove('is-open');
    this.toggle?.setAttribute('aria-expanded', 'false');
    this.modal.setAttribute('inert', '');
    this.modal.setAttribute('aria-hidden', 'true');
    document.body.removeEventListener('click', this.onBodyClick);
    document.body.classList.remove('overflow-hidden');
    removeTrapFocus(focusToggle ? this.toggle : null);
  }
}

if (!customElements.get('header-search')) {
  customElements.define('header-search', HeaderSearch);
}

class HeaderMegaMenuDrawer extends HTMLElement {
  connectedCallback() {
    this.headerDrawer = this.closest('header-drawer');
    this.details = this.headerDrawer?.querySelector('details');
    this.header = this.querySelector('.menu-drawer-mega__header');
    this.triggersList = this.querySelector('.menu-drawer-mega__triggers');
    this.closeButton = this.querySelector('.menu-drawer-mega__close');
    this.triggers = [...this.querySelectorAll('.menu-drawer-mega__trigger[role="tab"]')];
    this.panels = [...this.querySelectorAll('.menu-drawer-mega__panel')];
    this.submenuTriggers = [...this.querySelectorAll('.menu-drawer-mega__submenu-trigger')];
    this.submenus = [...this.querySelectorAll('.menu-drawer-mega__submenu')];
    this.backButtons = [...this.querySelectorAll('.menu-drawer-mega__back')];
    this.submenuCloseButtons = [...this.querySelectorAll('.menu-drawer-mega__submenu-close')];

    if (!this.triggers.length || !this.panels.length) return;

    this.activeTrigger = null;
    this.activeSubmenu = null;
    this.submenuAnimation = null;

    this.onTriggerClick = this.handleTriggerClick.bind(this);
    this.onCloseClick = this.handleCloseClick.bind(this);
    this.onSubmenuTriggerClick = this.handleSubmenuTriggerClick.bind(this);
    this.onBackClick = this.handleBackClick.bind(this);
    this.onSubmenuCloseClick = this.handleSubmenuCloseClick.bind(this);
    this.onDrawerToggle = this.handleDrawerToggle.bind(this);
    this.onTriggersScroll = this.updateTriggersOverflow.bind(this);
    this.onResize = this.updateTriggersOverflow.bind(this);

    this.triggers.forEach((trigger) => {
      trigger.addEventListener('click', this.onTriggerClick);
    });
    this.closeButton?.addEventListener('click', this.onCloseClick);
    this.submenuTriggers.forEach((trigger) => {
      trigger.addEventListener('click', this.onSubmenuTriggerClick);
    });
    this.backButtons.forEach((button) => {
      button.addEventListener('click', this.onBackClick);
    });
    this.submenuCloseButtons.forEach((button) => {
      button.addEventListener('click', this.onSubmenuCloseClick);
    });
    this.details?.addEventListener('toggle', this.onDrawerToggle);
    this.triggersList?.addEventListener('scroll', this.onTriggersScroll, { passive: true });
    window.addEventListener('resize', this.onResize);
    this.updateTriggersOverflow();
  }

  disconnectedCallback() {
    if (!this.triggers.length || !this.panels.length) return;

    this.triggers.forEach((trigger) => {
      trigger.removeEventListener('click', this.onTriggerClick);
    });
    this.closeButton?.removeEventListener('click', this.onCloseClick);
    this.submenuTriggers.forEach((trigger) => {
      trigger.removeEventListener('click', this.onSubmenuTriggerClick);
    });
    this.backButtons.forEach((button) => {
      button.removeEventListener('click', this.onBackClick);
    });
    this.submenuCloseButtons.forEach((button) => {
      button.removeEventListener('click', this.onSubmenuCloseClick);
    });
    this.details?.removeEventListener('toggle', this.onDrawerToggle);
    this.triggersList?.removeEventListener('scroll', this.onTriggersScroll);
    window.removeEventListener('resize', this.onResize);
    this.clearSubmenuAnimation();
  }

  updateTriggersOverflow() {
    if (!this.header || !this.triggersList) return;

    const { scrollWidth, clientWidth, scrollLeft } = this.triggersList;
    const hasOverflow = scrollWidth > clientWidth + 1;
    const atEnd = scrollLeft + clientWidth >= scrollWidth - 1;

    this.header.classList.toggle('has-triggers-overflow', hasOverflow && !atEnd);
  }

  handleDrawerToggle() {
    if (this.details?.open) {
      this.activateDefaultPanel();
      requestAnimationFrame(() => this.updateTriggersOverflow());
      return;
    }

    this.resetPanels();
    this.closeSubmenu(false);
  }

  activateDefaultPanel() {
    const activeTrigger =
      this.triggers.find((trigger) => trigger.dataset.childActive === 'true') || this.triggers[0];

    if (activeTrigger) {
      this.showPanel(activeTrigger.dataset.menuPanel);
      this.setActiveTrigger(activeTrigger);
      activeTrigger.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      requestAnimationFrame(() => this.updateTriggersOverflow());
    }
  }

  handleTriggerClick(event) {
    event.preventDefault();
    const trigger = event.currentTarget;
    const panelId = trigger.dataset.menuPanel;

    if (!panelId) return;

    if (this.activeTrigger === trigger) return;

    this.showPanel(panelId);
    this.setActiveTrigger(trigger);
    this.closeSubmenu(false);
  }

  showPanel(panelId) {
    this.panels.forEach((panel) => {
      panel.hidden = panel.id !== panelId;
    });
  }

  setActiveTrigger(trigger) {
    this.triggers.forEach((item) => {
      item.setAttribute('aria-selected', item === trigger ? 'true' : 'false');
    });
    this.activeTrigger = trigger;
  }

  resetPanels() {
    this.panels.forEach((panel) => {
      panel.hidden = true;
    });
    this.triggers.forEach((trigger) => {
      trigger.setAttribute('aria-selected', 'false');
    });
    this.activeTrigger = null;
  }

  handleSubmenuTriggerClick(event) {
    event.preventDefault();
    const trigger = event.currentTarget;
    const submenuId = trigger.getAttribute('aria-controls');
    const submenu = submenuId ? this.querySelector(`#${submenuId}`) : null;

    if (!submenu) return;

    this.openSubmenu(submenu, trigger);
  }

  openSubmenu(submenu, trigger) {
    if (this.activeSubmenu === submenu) return;

    this.closeSubmenu(false);
    this.activeSubmenu = submenu;
    this.activeSubmenuTrigger = trigger;
    this.classList.add('is-submenu-open');
    submenu.hidden = false;

    requestAnimationFrame(() => {
      submenu.classList.add('is-active');
    });

    submenu.querySelector('.menu-drawer-mega__back')?.focus();
  }

  handleBackClick(event) {
    event.preventDefault();
    this.closeSubmenu(true);
  }

  handleSubmenuCloseClick(event) {
    this.handleCloseClick(event);
  }

  closeSubmenu(animate = true) {
    if (!this.activeSubmenu) return;

    const submenu = this.activeSubmenu;
    const trigger = this.activeSubmenuTrigger;
    this.activeSubmenu = null;
    this.activeSubmenuTrigger = null;
    this.classList.remove('is-submenu-open');
    this.clearSubmenuAnimation();

    if (!animate) {
      submenu.classList.remove('is-active');
      submenu.hidden = true;
      return;
    }

    submenu.classList.remove('is-active');
    this.submenuAnimation = window.setTimeout(() => {
      if (!submenu.classList.contains('is-active')) {
        submenu.hidden = true;
      }
      this.submenuAnimation = null;
    }, 300);

    trigger?.focus();
  }

  clearSubmenuAnimation() {
    if (this.submenuAnimation) {
      window.clearTimeout(this.submenuAnimation);
      this.submenuAnimation = null;
    }
  }

  handleCloseClick(event) {
    event.preventDefault();
    const summary = this.details?.querySelector('summary');
    if (!this.headerDrawer || !summary) return;

    this.closeSubmenu(false);
    this.headerDrawer.closeMenuDrawer(event, summary);
  }
}

if (!customElements.get('header-mega-menu-drawer')) {
  customElements.define('header-mega-menu-drawer', HeaderMegaMenuDrawer);
}

class CardProductMedia extends HTMLElement {
  connectedCallback() {
    this.scrollContainer = this.querySelector('.card-product__media-inner');
    this.items = [...this.querySelectorAll('.card-product-media__item')];
    this.prevButton = this.querySelector('.js-back');
    this.nextButton = this.querySelector('.js-next');
    this.scrollbar = this.querySelector('.card-product__scrollbar');
    this.scrollbarThumb = this.querySelector('.card-product__scrollbar-thumb');

    if (!this.scrollContainer || !this.items.length || !this.prevButton || !this.nextButton) return;

    this.onPrevClick = this.handlePrevClick.bind(this);
    this.onNextClick = this.handleNextClick.bind(this);
    this.onScroll = () => {
      this.updateButtons();
      this.updateScrollbar();
      if (!this.colorMode || !this.colorReady) return;

      clearTimeout(this.colorSyncTimer);
      this.colorSyncTimer = setTimeout(() => {
        this.syncColor(this.getCurrentIndex());
      }, 80);
    };

    this.colorMode = this.items.some((item) => item.hasAttribute('data-color-url'));
    this.colorIndex = this.colorMode
      ? Math.max(0, this.items.findIndex((item) => item.hasAttribute('data-color-current')))
      : 0;
    this.colorReady = !this.colorMode || this.colorIndex === 0;
    this.colorSyncTimer = null;

    this.prevButton.addEventListener('click', this.onPrevClick);
    this.nextButton.addEventListener('click', this.onNextClick);
    this.scrollContainer.addEventListener('scroll', this.onScroll, { passive: true });

    this.resizeObserver = new ResizeObserver(this.onScroll);
    this.resizeObserver.observe(this.scrollContainer);

    if (this.colorMode && this.colorIndex > 0) {
      requestAnimationFrame(() => {
        const target = this.items[this.colorIndex];
        if (target) this.scrollContainer.scrollLeft = target.offsetLeft;
        this.colorReady = true;
        this.updateButtons();
        this.updateScrollbar();
      });
    }

    this.updateButtons();
    this.updateScrollbar();
  }

  disconnectedCallback() {
    if (!this.scrollContainer || !this.items?.length || !this.prevButton || !this.nextButton) return;

    this.prevButton.removeEventListener('click', this.onPrevClick);
    this.nextButton.removeEventListener('click', this.onNextClick);
    this.scrollContainer.removeEventListener('scroll', this.onScroll);
    this.resizeObserver?.disconnect();
    clearTimeout(this.colorSyncTimer);
  }

  getCurrentIndex() {
    const scrollLeft = this.scrollContainer.scrollLeft;
    let closestIndex = 0;
    let closestDistance = Infinity;

    this.items.forEach((item, index) => {
      const distance = Math.abs(item.offsetLeft - scrollLeft);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    return closestIndex;
  }

  scrollToIndex(index) {
    const targetIndex = Math.max(0, Math.min(index, this.items.length - 1));
    const target = this.items[targetIndex];
    if (!target) return;

    this.scrollContainer.scrollTo({
      left: target.offsetLeft,
      behavior: 'smooth',
    });
  }

  handlePrevClick(event) {
    event.preventDefault();
    event.stopPropagation();
    if (this.prevButton.classList.contains('disabled')) return;

    const index = (this.colorMode ? this.colorIndex : this.getCurrentIndex()) - 1;
    this.scrollToIndex(index);
    this.syncColor(index);
  }

  handleNextClick(event) {
    event.preventDefault();
    event.stopPropagation();
    if (this.nextButton.classList.contains('disabled')) return;

    const index = (this.colorMode ? this.colorIndex : this.getCurrentIndex()) + 1;
    this.scrollToIndex(index);
    this.syncColor(index);
  }

  syncColor(index) {
    if (!this.colorMode) return;

    const nextIndex = Math.max(0, Math.min(index, this.items.length - 1));
    if (nextIndex === this.colorIndex) return;

    this.colorIndex = nextIndex;
    const card = this.closest('.card-product');
    const slide = this.items[nextIndex];
    if (!card || !slide) return;

    if (slide.dataset.colorUrl) card.setAttribute('href', slide.dataset.colorUrl);

    card.querySelectorAll('[data-color-panel]').forEach((panel) => {
      panel.classList.toggle('is-active', Number(panel.dataset.colorPanel) === nextIndex);
    });

    card.querySelectorAll('card-color-swatch').forEach((swatch) => {
      swatch.classList.toggle('active', Number(swatch.dataset.colorIndex) === nextIndex);
    });

    this.updateButtons();
  }

  updateButtons() {
    const index = this.colorMode ? this.colorIndex : this.getCurrentIndex();
    const lastIndex = this.items.length - 1;

    this.prevButton.classList.toggle('disabled', index <= 0);
    this.nextButton.classList.toggle('disabled', index >= lastIndex);
  }

  updateScrollbar() {
    if (!this.scrollbar || !this.scrollbarThumb) return;

    if (this.items.length <= 1) {
      this.scrollbar.hidden = true;
      return;
    }

    const { scrollLeft, scrollWidth, clientWidth } = this.scrollContainer;
    const maxScroll = scrollWidth - clientWidth;

    if (maxScroll <= 0) {
      this.scrollbar.hidden = true;
      return;
    }

    this.scrollbar.hidden = false;

    const trackWidth = this.scrollbar.clientWidth;
    const thumbWidth = Math.max((clientWidth / scrollWidth) * trackWidth, 2);
    const thumbOffset = (scrollLeft / maxScroll) * (trackWidth - thumbWidth);

    this.scrollbarThumb.style.width = `${thumbWidth}px`;
    this.scrollbarThumb.style.transform = `translateX(${thumbOffset}px)`;
  }
}

if (!customElements.get('card-product-media')) {
  customElements.define('card-product-media', CardProductMedia);
}

class ProductMediaGalleryScroll extends HTMLElement {
  connectedCallback() {
    this.slider = this.querySelector('[id^="Slider-"]');
    this.scrollContainer = this.slider;
    this.items = [...this.querySelectorAll('[id^="Slide-"]')];
    this.sliderItems = this.items;
    this.prevButton = this.querySelector('.js-back');
    this.nextButton = this.querySelector('.js-next');
    this.scrollbar = this.querySelector('.product-media-gallery-scroll__scrollbar');
    this.scrollbarThumb = this.querySelector('.product-media-gallery-scroll__scrollbar-thumb');

    if (!this.scrollContainer || !this.items.length) return;

    this.onPrevClick = this.handlePrevClick.bind(this);
    this.onNextClick = this.handleNextClick.bind(this);
    this.onScroll = () => {
      this.updateButtons();
      this.updateScrollbar();
      this.dispatchSlideChanged();
    };

    this.prevButton?.addEventListener('click', this.onPrevClick);
    this.nextButton?.addEventListener('click', this.onNextClick);
    this.scrollContainer.addEventListener('scroll', this.onScroll, { passive: true });

    this.resizeObserver = new ResizeObserver(this.onScroll);
    this.resizeObserver.observe(this.scrollContainer);

    this.updateButtons();
    this.updateScrollbar();
  }

  disconnectedCallback() {
    if (!this.scrollContainer) return;

    this.prevButton?.removeEventListener('click', this.onPrevClick);
    this.nextButton?.removeEventListener('click', this.onNextClick);
    this.scrollContainer.removeEventListener('scroll', this.onScroll);
    this.resizeObserver?.disconnect();
  }

  resetPages() {
    this.items = [...this.querySelectorAll('[id^="Slide-"]')];
    this.sliderItems = this.items;
    this.updateButtons();
    this.updateScrollbar();
  }

  getCurrentIndex() {
    const scrollLeft = this.scrollContainer.scrollLeft;
    let closestIndex = 0;
    let closestDistance = Infinity;

    this.items.forEach((item, index) => {
      const distance = Math.abs(item.offsetLeft - scrollLeft);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    return closestIndex;
  }

  scrollToIndex(index) {
    const targetIndex = Math.max(0, Math.min(index, this.items.length - 1));
    const target = this.items[targetIndex];
    if (!target) return;

    this.scrollContainer.scrollTo({
      left: target.offsetLeft,
      behavior: 'smooth',
    });
  }

  handlePrevClick(event) {
    event.preventDefault();
    if (this.prevButton?.classList.contains('disabled')) return;

    this.scrollToIndex(this.getCurrentIndex() - 1);
  }

  handleNextClick(event) {
    event.preventDefault();
    if (this.nextButton?.classList.contains('disabled')) return;

    this.scrollToIndex(this.getCurrentIndex() + 1);
  }

  updateButtons() {
    if (!this.prevButton || !this.nextButton || !this.items.length) return;

    const index = this.getCurrentIndex();
    const lastIndex = this.items.length - 1;

    this.prevButton.classList.toggle('disabled', index <= 0);
    this.nextButton.classList.toggle('disabled', index >= lastIndex);
  }

  updateScrollbar() {
    if (!this.scrollbar || !this.scrollbarThumb) return;

    if (this.items.length <= 1) {
      this.scrollbar.hidden = true;
      return;
    }

    const { scrollLeft, scrollWidth, clientWidth } = this.scrollContainer;
    const maxScroll = scrollWidth - clientWidth;

    if (maxScroll <= 0) {
      this.scrollbar.hidden = true;
      return;
    }

    this.scrollbar.hidden = false;

    const trackWidth = this.scrollbar.clientWidth;
    const thumbWidth = Math.max((clientWidth / scrollWidth) * trackWidth, 2);
    const thumbOffset = (scrollLeft / maxScroll) * (trackWidth - thumbWidth);

    this.scrollbarThumb.style.width = `${thumbWidth}px`;
    this.scrollbarThumb.style.transform = `translateX(${thumbOffset}px)`;
  }

  dispatchSlideChanged() {
    const currentIndex = this.getCurrentIndex();
    const currentElement = this.items[currentIndex];
    if (!currentElement || currentIndex === this.currentPage) return;

    this.currentPage = currentIndex;
    this.dispatchEvent(
      new CustomEvent('slideChanged', {
        detail: {
          currentPage: currentIndex + 1,
          currentElement,
        },
      })
    );
  }
}

if (!customElements.get('product-media-gallery-scroll')) {
  customElements.define('product-media-gallery-scroll', ProductMediaGalleryScroll);
}

class FooterAccordion extends HTMLElement {
  connectedCallback() {
    this.items = [...this.querySelectorAll('[data-accordion-item]')];
    if (!this.items.length) return;

    this.mobileQuery = window.matchMedia('(max-width: 989px)');
    this.onTriggerClick = this.handleTriggerClick.bind(this);
    this.onBreakpointChange = this.handleBreakpointChange.bind(this);

    this.items.forEach((item) => {
      const trigger = item.querySelector('[data-accordion-trigger]');
      const panel = item.querySelector('[data-accordion-panel]');
      if (!trigger || !panel) return;

      item._trigger = trigger;
      item._panel = panel;
      trigger.addEventListener('click', this.onTriggerClick);
    });

    this.mobileQuery.addEventListener('change', this.onBreakpointChange);
    this.handleBreakpointChange();
  }

  disconnectedCallback() {
    this.items?.forEach((item) => {
      item._trigger?.removeEventListener('click', this.onTriggerClick);
    });
    this.mobileQuery?.removeEventListener('change', this.onBreakpointChange);
  }

  handleBreakpointChange() {
    if (this.mobileQuery.matches) {
      this.resetMobileState();
      return;
    }

    this.resetDesktopState();
  }

  resetMobileState() {
    this.items.forEach((item) => {
      const { _panel, _trigger } = item;
      _trigger.setAttribute('aria-expanded', 'false');
      item.classList.remove('is-open');
      _panel.hidden = true;
      _panel.style.height = '0px';
      _panel.style.overflow = 'hidden';
    });
  }

  resetDesktopState() {
    this.items.forEach((item) => {
      const { _panel, _trigger } = item;
      _panel.hidden = false;
      _panel.style.height = '';
      _panel.style.overflow = '';
      _trigger.setAttribute('aria-expanded', 'true');
      item.classList.remove('is-open');
    });
  }

  handleTriggerClick(event) {
    if (!this.mobileQuery.matches) return;

    const item = event.currentTarget.closest('[data-accordion-item]');
    if (!item?._panel || !item._trigger) return;

    const isOpen = item._trigger.getAttribute('aria-expanded') === 'true';

    if (isOpen) {
      this.closeItem(item);
      return;
    }

    this.items.forEach((other) => {
      if (other !== item && other._trigger?.getAttribute('aria-expanded') === 'true') {
        this.closeItem(other);
      }
    });

    this.openItem(item);
  }

  openItem(item) {
    const { _panel, _trigger } = item;

    _panel.hidden = false;
    _panel.style.overflow = 'hidden';
    _panel.style.height = '0px';
    _trigger.setAttribute('aria-expanded', 'true');
    item.classList.add('is-open');

    requestAnimationFrame(() => {
      _panel.style.height = `${_panel.scrollHeight}px`;
    });

    const onTransitionEnd = (event) => {
      if (event.propertyName !== 'height' || _trigger.getAttribute('aria-expanded') !== 'true') return;
      _panel.style.height = 'auto';
      _panel.style.overflow = '';
      _panel.removeEventListener('transitionend', onTransitionEnd);
    };

    _panel.addEventListener('transitionend', onTransitionEnd);
  }

  closeItem(item) {
    const { _panel, _trigger } = item;

    _panel.style.overflow = 'hidden';
    _panel.style.height = `${_panel.scrollHeight}px`;
    _trigger.setAttribute('aria-expanded', 'false');
    item.classList.remove('is-open');

    requestAnimationFrame(() => {
      _panel.style.height = '0px';
    });

    const onTransitionEnd = (event) => {
      if (event.propertyName !== 'height' || _trigger.getAttribute('aria-expanded') !== 'false') return;
      _panel.hidden = true;
      _panel.style.height = '';
      _panel.style.overflow = '';
      _panel.removeEventListener('transitionend', onTransitionEnd);
    };

    _panel.addEventListener('transitionend', onTransitionEnd);
  }
}

if (!customElements.get('footer-accordion')) {
  customElements.define('footer-accordion', FooterAccordion);
}

class PaginationLoadMore extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-load-more]');
    this.onClick = this.handleClick.bind(this);
    this.button?.addEventListener('click', this.onClick);
    this.setupIntersectionObserver();
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.onClick);
    this.observer?.disconnect();
  }

  setupIntersectionObserver() {
    if (!('IntersectionObserver' in window)) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this.loadMore();
        }
      },
      { rootMargin: '200px 0px', threshold: 0 }
    );

    this.observer.observe(this);
  }

  buildFetchUrl(nextUrl) {
    const url = new URL(nextUrl, window.location.origin);
    url.searchParams.set('section_id', this.dataset.sectionId);
    return url.toString();
  }

  setLoading(isLoading) {
    this.loading = isLoading;
    if (this.button) {
      this.button.disabled = isLoading;
      this.button.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    }
    this.classList.toggle('is-loading', isLoading);
  }

  handleClick(event) {
    event.preventDefault();
    this.loadMore();
  }

  async loadMore() {
    if (this.loading || !this.dataset.nextUrl || !this.dataset.sectionId) return;

    const gridId = this.dataset.gridId || 'product-grid';
    const currentGrid = document.getElementById(gridId);

    if (!currentGrid) return;

    this.setLoading(true);
    this.observer?.disconnect();

    try {
      const response = await fetch(this.buildFetchUrl(this.dataset.nextUrl));
      const responseText = await response.text();
      const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
      const newGrid = parsedHTML.getElementById(gridId);

      if (newGrid) {
        [...newGrid.children].forEach((child) => {
          currentGrid.appendChild(child);
        });

        currentGrid.querySelectorAll('.scroll-trigger').forEach((element) => {
          element.classList.add('scroll-trigger--cancel');
        });
      }

      const newPagination = parsedHTML.querySelector('pagination-load-more');
      this.setLoading(false);

      if (newPagination) {
        this.replaceWith(document.importNode(newPagination, true));
      } else {
        this.remove();
      }

      if (typeof initializeScrollAnimationTrigger === 'function') {
        initializeScrollAnimationTrigger(responseText);
      }
    } catch (error) {
      console.error(error);
      this.setLoading(false);
      this.setupIntersectionObserver();
    }
  }
}

if (!customElements.get('pagination-load-more')) {
  customElements.define('pagination-load-more', PaginationLoadMore);
}


class FaqItem extends HTMLElement {
  connectedCallback() {
    this.trigger = this.querySelector('[data-faq-trigger]');
    this.panel = this.querySelector('[data-faq-panel]');
    if (!this.trigger || !this.panel) return;

    this.isOpen = false;
    this.onTriggerClick = this.onTriggerClick.bind(this);
    this.trigger.addEventListener('click', this.onTriggerClick);

    if (this.dataset.open === 'true') {
      this.open(false);
      return;
    }

    this.close(false);
  }

  disconnectedCallback() {
    this.trigger?.removeEventListener('click', this.onTriggerClick);
  }

  onTriggerClick() {
    if (this.isOpen) {
      this.close();
      return;
    }

    this.closeSiblings();
    this.open();
  }

  closeSiblings() {
    const group = this.closest('.faqs__block');
    if (!group) return;

    group.querySelectorAll('faq-item').forEach((item) => {
      if (item !== this) item.close();
    });
  }

  open(animate = true) {
    if (!this.panel || !this.trigger) return;

    this.isOpen = true;
    this.classList.add('is-open');
    this.trigger.setAttribute('aria-expanded', 'true');
    this.panel.hidden = false;

    if (!animate) {
      this.panel.style.height = 'auto';
      this.panel.style.overflow = '';
      return;
    }

    this.panel.style.overflow = 'hidden';
    this.panel.style.height = '0px';

    requestAnimationFrame(() => {
      this.panel.style.height = `${this.panel.scrollHeight}px`;
    });

    const onTransitionEnd = (event) => {
      if (event.propertyName !== 'height' || !this.isOpen) return;
      this.panel.style.height = 'auto';
      this.panel.style.overflow = '';
      this.panel.removeEventListener('transitionend', onTransitionEnd);
    };

    this.panel.addEventListener('transitionend', onTransitionEnd);
  }

  close(animate = true) {
    if (!this.panel || !this.trigger) return;

    this.isOpen = false;
    this.classList.remove('is-open');
    this.trigger.setAttribute('aria-expanded', 'false');

    if (!animate) {
      this.panel.hidden = true;
      this.panel.style.height = '';
      this.panel.style.overflow = '';
      return;
    }

    this.panel.style.overflow = 'hidden';
    this.panel.style.height = `${this.panel.scrollHeight}px`;

    requestAnimationFrame(() => {
      this.panel.style.height = '0px';
    });

    const onTransitionEnd = (event) => {
      if (event.propertyName !== 'height' || this.isOpen) return;
      this.panel.hidden = true;
      this.panel.style.height = '';
      this.panel.style.overflow = '';
      this.panel.removeEventListener('transitionend', onTransitionEnd);
    };

    this.panel.addEventListener('transitionend', onTransitionEnd);
  }
}

if (!customElements.get('faq-item')) {
  customElements.define('faq-item', FaqItem);
}

class FaqsSection extends HTMLElement {
  connectedCallback() {
    this.blocks = this.querySelectorAll('.faqs__block');
    this.sidebarItems = this.querySelectorAll('.faqs__sidebar-item');
    this.onSidebarClick = this.onSidebarClick.bind(this);

    this.sidebarItems.forEach((item) => {
      item.addEventListener('click', this.onSidebarClick);
    });

    const hash = window.location.hash.slice(1);
    if (hash && this.querySelector(`.faqs__block[data-id="${hash}"]`)) {
      this.showBlock(hash);
    }
  }

  disconnectedCallback() {
    this.sidebarItems.forEach((item) => {
      item.removeEventListener('click', this.onSidebarClick);
    });
  }

  onSidebarClick(event) {
    event.preventDefault();
    const targetId = event.currentTarget.dataset.target;
    if (!targetId) return;

    this.showBlock(targetId);
    history.replaceState(null, '', `#${targetId}`);
  }

  showBlock(id) {
    this.blocks.forEach((block) => {
      block.classList.toggle('hidden', block.dataset.id !== id);
    });

    this.sidebarItems.forEach((item) => {
      item.classList.toggle('is-active', item.dataset.target === id);
    });
  }
}

if (!customElements.get('faqs-section')) {
  customElements.define('faqs-section', FaqsSection);
}

class AnimatedAccordion extends HTMLElement {
  static get observedAttributes() {
    return ['open'];
  }

  connectedCallback() {
    this.trigger = this.querySelector('[data-accordion-trigger]');
    this.panel = this.querySelector('[data-accordion-panel]');
    if (!this.trigger || !this.panel) return;

    this.isOpen = false;
    this.isReady = false;
    this.onTriggerClick = this.onTriggerClick.bind(this);
    this.trigger.addEventListener('click', this.onTriggerClick);

    if (!this.panel.id) {
      this.panel.id = `AnimatedAccordion-panel-${Date.now()}-${Math.floor(Math.random() * 10000)}`;
    }

    this.trigger.setAttribute('aria-controls', this.panel.id);

    if (this.hasAttribute('open') || this.dataset.open === 'true') {
      this.open(false);
    } else {
      this.close(false);
    }

    this.isReady = true;
  }

  disconnectedCallback() {
    this.trigger?.removeEventListener('click', this.onTriggerClick);
  }

  attributeChangedCallback(name, _oldValue, newValue) {
    if (name !== 'open' || !this.isReady || !this.trigger || !this.panel) return;

    const shouldOpen = newValue !== null;
    if (shouldOpen === this.isOpen) return;

    if (shouldOpen) {
      this.closeSiblings();
      this.open();
      return;
    }

    this.close();
  }

  get open() {
    return this.isOpen;
  }

  set open(value) {
    if (value) {
      this.setAttribute('open', '');
      return;
    }

    this.removeAttribute('open');
  }

  onTriggerClick(event) {
    event.preventDefault();
    this.toggle();
  }

  closeSiblings() {
    const group = this.dataset.group;
    if (!group) return;

    const scope = this.parentElement || document;
    scope.querySelectorAll(`animated-accordion[data-group="${group}"]`).forEach((item) => {
      if (item !== this && item.isOpen) item.close();
    });
  }

  open(animate = true) {
    if (!this.panel || !this.trigger || this.isOpen) return;

    this.isOpen = true;
    this.classList.add('is-open');
    this.trigger.setAttribute('aria-expanded', 'true');

    if (!this.hasAttribute('open')) {
      this.setAttribute('open', '');
    }

    this.panel.hidden = false;

    if (animate) {
      this.dispatchEvent(new CustomEvent('toggle', { detail: { open: true }, bubbles: true }));
    }

    if (!animate || this.prefersReducedMotion()) {
      this.panel.style.height = 'auto';
      this.panel.style.overflow = '';
      return;
    }

    this.panel.style.overflow = 'hidden';
    this.panel.style.height = '0px';

    requestAnimationFrame(() => {
      this.panel.style.height = `${this.panel.scrollHeight}px`;
    });

    const onTransitionEnd = (event) => {
      if (event.target !== this.panel || event.propertyName !== 'height' || !this.isOpen) return;
      this.panel.style.height = 'auto';
      this.panel.style.overflow = '';
      this.panel.removeEventListener('transitionend', onTransitionEnd);
    };

    this.panel.addEventListener('transitionend', onTransitionEnd);
  }

  close(animate = true) {
    if (!this.panel || !this.trigger || (!this.isOpen && animate)) return;

    this.isOpen = false;
    this.classList.remove('is-open');
    this.trigger.setAttribute('aria-expanded', 'false');

    if (this.hasAttribute('open')) {
      this.removeAttribute('open');
    }

    if (animate) {
      this.dispatchEvent(new CustomEvent('toggle', { detail: { open: false }, bubbles: true }));
    }

    if (!animate || this.prefersReducedMotion()) {
      this.panel.hidden = true;
      this.panel.style.height = '';
      this.panel.style.overflow = '';
      return;
    }

    this.panel.style.overflow = 'hidden';
    this.panel.style.height = `${this.panel.scrollHeight}px`;

    requestAnimationFrame(() => {
      this.panel.style.height = '0px';
    });

    const onTransitionEnd = (event) => {
      if (event.target !== this.panel || event.propertyName !== 'height' || this.isOpen) return;
      this.panel.hidden = true;
      this.panel.style.height = '';
      this.panel.style.overflow = '';
      this.panel.removeEventListener('transitionend', onTransitionEnd);
    };

    this.panel.addEventListener('transitionend', onTransitionEnd);
  }

  toggle(force) {
    const shouldOpen = typeof force === 'boolean' ? force : !this.isOpen;
    if (shouldOpen) {
      this.closeSiblings();
      this.open();
      return;
    }

    this.close();
  }

  prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
}

if (!customElements.get('animated-accordion')) {
  customElements.define('animated-accordion', AnimatedAccordion);
}

class ColorSwatch extends HTMLElement {
  connectedCallback() {
    this.url = this.dataset.url;
    this.sectionId = this.dataset.sectionId;

    this.addEventListener('click', this.handleClick);
  }

  handleClick(event) {
    fetch(this.url + "?section_id=" + this.sectionId)
    .then(response => response.text())
    .then(text => {
      const parser = new DOMParser();
      const doc = parser.parseFromString(text, 'text/html');
      const newProduct = doc.querySelector('.product');
      if (newProduct) {
        document.querySelector('.product').innerHTML = newProduct.innerHTML;
        window.history.pushState(null, '', this.url);
      }
    });
  }
}

if (!customElements.get('color-swatch')) {
  customElements.define('color-swatch', ColorSwatch);
}

class CardColorSwatch extends HTMLElement {
  connectedCallback() {
    this.url = this.dataset.url;
    this.onClick = this.handleClick.bind(this);
    this.addEventListener('click', this.onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
  }

  handleClick(event) {
    event.preventDefault();
    event.stopPropagation();

    const card = this.closest('.card-product');
    const media = card?.querySelector('card-product-media');
    const index = Number(this.dataset.colorIndex);

    if (media?.colorMode && Number.isInteger(index)) {
      media.scrollToIndex(index);
      media.syncColor(index);
      return;
    }

    if (!card || !this.url) return;

    fetch(`${this.url}?section_id=card-product`)
      .then((response) => response.text())
      .then((text) => {
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const newProduct = doc.querySelector('.card-product');
        if (!newProduct || !card.isConnected) return;

        card.replaceWith(document.importNode(newProduct, true));
      });
  }
}

if (!customElements.get('card-color-swatch')) {
  customElements.define('card-color-swatch', CardColorSwatch);
}

const RECENTLY_VIEWED_KEY = 'recently-viewed';
const RECENTLY_VIEWED_MAX = 12;

function getRecentlyViewedHandles() {
  try {
    const stored = JSON.parse(localStorage.getItem(RECENTLY_VIEWED_KEY));
    return Array.isArray(stored) ? stored.filter(Boolean) : [];
  } catch (error) {
    return [];
  }
}

function setRecentlyViewedHandles(handles) {
  localStorage.setItem(RECENTLY_VIEWED_KEY, JSON.stringify(handles));
}

function recordRecentlyViewed(handle) {
  if (!handle) return;

  const next = getRecentlyViewedHandles().filter((item) => item !== handle);
  next.unshift(handle);
  setRecentlyViewedHandles(next.slice(0, RECENTLY_VIEWED_MAX));
}

function getProductHandleFromUrl(url) {
  if (!url) return null;
  const match = String(url).match(/\/products\/([^/?#]+)/);
  return match ? decodeURIComponent(match[1]) : null;
}

function trackCurrentProductView() {
  const productInfo = document.querySelector('product-info[data-url]');
  if (!productInfo) return;

  const handle =
    getProductHandleFromUrl(productInfo.dataset.url) ||
    getProductHandleFromUrl(window.location.pathname);

  recordRecentlyViewed(handle);
}

trackCurrentProductView();

class RecentlyViewed extends HTMLElement {
  connectedCallback() {
    this.inner = this.querySelector('.recently-viewed__inner');
    this.limit = Number(this.dataset.limit) || 8;
    this.exclude = this.dataset.exclude || '';
    this.abortController = new AbortController();
    this.render();
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  getHandles() {
    return getRecentlyViewedHandles()
      .filter((handle) => handle !== this.exclude)
      .slice(0, this.limit);
  }

  render() {
    const handles = this.getHandles();

    if (!handles.length || !this.inner) {
      this.hidden = true;
      return;
    }

    const root = window.Shopify?.routes?.root || '/';
    const params = new URLSearchParams({
      q: handles.join(','),
      type: 'product',
      section_id: 'recently-viewed',
    });

    fetch(`${root}search?${params}`, { signal: this.abortController.signal })
      .then((response) => {
        if (!response.ok) throw new Error(response.statusText);
        return response.text();
      })
      .then((text) => {
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const sectionInner = doc.querySelector('.recently-viewed__inner');

        if (!sectionInner?.childElementCount) {
          this.hidden = true;
          return;
        }

        // Preserve localStorage order (search/all_products may not match).
        const cardsByHandle = new Map(
          [...sectionInner.querySelectorAll('[data-product-handle]')].map((item) => [
            item.dataset.productHandle,
            item,
          ])
        );

        this.inner.replaceChildren(
          ...handles.map((handle) => cardsByHandle.get(handle)).filter(Boolean)
        );

        if (!this.inner.childElementCount) {
          this.hidden = true;
          return;
        }

        this.hidden = false;
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        this.hidden = true;
      });
  }
}

if (!customElements.get('recently-viewed')) {
  customElements.define('recently-viewed', RecentlyViewed);
}


class GridToggler extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.btns = this.querySelectorAll('button');
    this.btns.forEach(btn => {
      btn.addEventListener('click', (event)=> this.handleClick(event));
    });
  }

  handleClick(event) {
    event.preventDefault();
    this.btns.forEach(btn => {
      btn.classList.remove('active');
    });
    event.currentTarget.classList.add('active');
    this.closest('.section-collection').querySelector('.product-grid').classList.remove('one-column');
    this.closest('.section-collection').querySelector('.product-grid').classList.add(event.currentTarget.dataset.class);
  }
}

if (!customElements.get('grid-toggler')) {
  customElements.define('grid-toggler', GridToggler);
}


const WISHLIST_KEY = 'wishlist';
const WISHLIST_SECTION = 'wishlist-grid';
const WISHLIST_FETCH_BATCH = 20;

function getWishlistHandles() {
  try {
    const stored = JSON.parse(localStorage.getItem(WISHLIST_KEY));
    return Array.isArray(stored) ? stored.filter(Boolean) : [];
  } catch (error) {
    return [];
  }
}

function setWishlistHandles(handles) {
  localStorage.setItem(WISHLIST_KEY, JSON.stringify(handles));
  window.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { handles } }));
}

window.addEventListener('storage', (event) => {
  if (event.key === WISHLIST_KEY) {
    window.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { handles: getWishlistHandles() } }));
  }
});

class BtnWishlist extends HTMLElement {
  connectedCallback() {
    this.handle = this.dataset.handle;
    this.button = this.querySelector('button');
    this.onClick = this.handleClick.bind(this);
    this.onWishlistUpdated = this.syncState.bind(this);

    this.syncState();
    this.addEventListener('click', this.onClick);
    window.addEventListener('wishlist:updated', this.onWishlistUpdated);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    window.removeEventListener('wishlist:updated', this.onWishlistUpdated);
  }

  syncState() {
    this.added = getWishlistHandles().includes(this.handle);
    this.classList.toggle('is-active', this.added);
    if (this.button) {
      this.button.setAttribute('aria-pressed', this.added ? 'true' : 'false');
    }
  }

  handleClick(event) {
    event.preventDefault();

    const handles = getWishlistHandles().filter((item) => item !== this.handle);

    if (this.added) {
      setWishlistHandles(handles);
    } else {
      setWishlistHandles([this.handle, ...handles]);
    }

    this.syncState();
  }
}

if (!customElements.get('btn-wishlist')) {
  customElements.define('btn-wishlist', BtnWishlist);
}

class WishlistGrid extends HTMLElement {
  connectedCallback() {
    this.inner = this.querySelector('.wishlist__inner');
    this.emptyState = this.querySelector('.wishlist__empty');
    this.showEmpty = this.dataset.showEmpty !== 'false';
    this.abortController = new AbortController();
    this.onWishlistUpdated = () => this.render();

    window.addEventListener('wishlist:updated', this.onWishlistUpdated);
    this.render();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    window.removeEventListener('wishlist:updated', this.onWishlistUpdated);
  }

  fetchWishlistBatch(handles, signal) {
    const root = window.Shopify?.routes?.root || '/';
    const params = new URLSearchParams({
      q: handles.join(','),
      type: 'product',
      section_id: WISHLIST_SECTION,
    });

    return fetch(`${root}search?${params}`, { signal })
      .then((response) => {
        if (!response.ok) throw new Error(response.statusText);
        return response.text();
      })
      .then((text) => {
        const doc = new DOMParser().parseFromString(text, 'text/html');
        return doc.querySelector('.wishlist__inner');
      });
  }

  render() {
    this.abortController?.abort();
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    const handles = getWishlistHandles();

    if (!handles.length) {
      this.inner?.replaceChildren();
      this.classList.add('wishlist--empty');
      if (this.emptyState) this.emptyState.hidden = !this.showEmpty;
      return;
    }

    const batches = [];
    for (let index = 0; index < handles.length; index += WISHLIST_FETCH_BATCH) {
      batches.push(handles.slice(index, index + WISHLIST_FETCH_BATCH));
    }

    Promise.all(batches.map((batch) => this.fetchWishlistBatch(batch, signal)))
      .then((sections) => {
        if (signal.aborted) return;

        const cardsByHandle = new Map();
        sections.filter(Boolean).forEach((sectionInner) => {
          sectionInner.querySelectorAll('[data-product-handle]').forEach((item) => {
            cardsByHandle.set(item.dataset.productHandle, item);
          });
        });

        const orderedCards = handles.map((handle) => cardsByHandle.get(handle)).filter(Boolean);

        if (!orderedCards.length) {
          this.inner?.replaceChildren();
          this.classList.add('wishlist--empty');
          if (this.emptyState) this.emptyState.hidden = !this.showEmpty;
          return;
        }

        this.classList.remove('wishlist--empty');
        if (this.emptyState) this.emptyState.hidden = true;
        this.inner?.replaceChildren(...orderedCards);
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        this.classList.add('wishlist--empty');
        if (this.emptyState) this.emptyState.hidden = !this.showEmpty;
      });
  }
}

if (!customElements.get('wishlist-grid')) {
  customElements.define('wishlist-grid', WishlistGrid);
}

class ProductDetailInfo extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('.product-detail-info__button');
    this.popup = this.querySelector('.product-detail-info__popup');
    this.closeButton = this.querySelector('.product-detail-info__close');
    if (!this.button || !this.popup) return;

    this.mql = window.matchMedia('(min-width: 750px)');
    this.isDesktop = this.mql.matches;

    this.onPointerEnter = () => {
      if (this.isDesktop) this.open();
    };
    this.onPointerLeave = () => {
      if (this.isDesktop) this.close();
    };
    this.onFocusIn = () => {
      if (this.isDesktop) this.open();
    };
    this.onFocusOut = (event) => {
      if (this.isDesktop && !this.contains(event.relatedTarget)) this.close();
    };
    this.onButtonClick = (event) => {
      if (this.isDesktop) return;

      event.preventDefault();
      event.stopPropagation();
      this.toggle();
    };
    this.onCloseClick = (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.close();
    };
    this.onDocumentPointerDown = (event) => {
      if (this.isDesktop || !this.classList.contains('is-open')) return;

      const panel = this.querySelector('.product-detail-info__panel');
      if (panel?.contains(event.target) || this.button.contains(event.target)) {
        this.outsideTap = null;
        return;
      }

      this.outsideTap = { x: event.clientX, y: event.clientY };
    };
    this.onDocumentPointerUp = (event) => {
      if (!this.outsideTap) return;

      const dx = Math.abs(event.clientX - this.outsideTap.x);
      const dy = Math.abs(event.clientY - this.outsideTap.y);
      this.outsideTap = null;
      if (dx < 10 && dy < 10) this.close();
    };
    this.onDocumentPointerCancel = () => {
      this.outsideTap = null;
    };
    this.onKeyDown = (event) => {
      if (event.key === 'Escape' && this.classList.contains('is-open')) this.close();
    };
    this.onBreakpointChange = (event) => {
      this.isDesktop = event.matches;
      this.close();
      this.updateMode();
    };

    this.updateMode();

    this.mql.addEventListener('change', this.onBreakpointChange);
    this.button.addEventListener('click', this.onButtonClick);
    this.closeButton?.addEventListener('click', this.onCloseClick);
    document.addEventListener('pointerdown', this.onDocumentPointerDown);
    document.addEventListener('pointerup', this.onDocumentPointerUp);
    document.addEventListener('pointercancel', this.onDocumentPointerCancel);
    document.addEventListener('keydown', this.onKeyDown);
  }

  disconnectedCallback() {
    this.mql?.removeEventListener('change', this.onBreakpointChange);
    this.removeEventListener('pointerenter', this.onPointerEnter);
    this.removeEventListener('pointerleave', this.onPointerLeave);
    this.removeEventListener('focusin', this.onFocusIn);
    this.removeEventListener('focusout', this.onFocusOut);
    this.button?.removeEventListener('click', this.onButtonClick);
    this.closeButton?.removeEventListener('click', this.onCloseClick);
    document.removeEventListener('pointerdown', this.onDocumentPointerDown);
    document.removeEventListener('pointerup', this.onDocumentPointerUp);
    document.removeEventListener('pointercancel', this.onDocumentPointerCancel);
    document.removeEventListener('keydown', this.onKeyDown);
  }

  updateMode() {
    this.removeEventListener('pointerenter', this.onPointerEnter);
    this.removeEventListener('pointerleave', this.onPointerLeave);
    this.removeEventListener('focusin', this.onFocusIn);
    this.removeEventListener('focusout', this.onFocusOut);

    if (this.isDesktop) {
      this.addEventListener('pointerenter', this.onPointerEnter);
      this.addEventListener('pointerleave', this.onPointerLeave);
      this.addEventListener('focusin', this.onFocusIn);
      this.addEventListener('focusout', this.onFocusOut);
    }
  }

  open() {
    this.button.setAttribute('aria-expanded', 'true');
    this.popup.removeAttribute('hidden');

    if (this.isDesktop) {
      this.classList.add('is-open');
      return;
    }

    this.popup.offsetHeight;
    this.classList.add('is-open');
  }

  close() {
    this.classList.remove('is-open');
    this.button.setAttribute('aria-expanded', 'false');
    this.popup.setAttribute('hidden', '');
    this.outsideTap = null;
  }

  toggle() {
    if (this.classList.contains('is-open')) {
      this.close();
    } else {
      this.open();
    }
  }
}

if (!customElements.get('product-detail-info')) {
  customElements.define('product-detail-info', ProductDetailInfo);
}

class FacetSort extends HTMLElement {
  connectedCallback() {
    this.onChange = (event) => {
      const input = event.target;
      if (!(input instanceof HTMLInputElement) || input.name !== 'sort_by') return;

      const label = this.querySelector('[data-sort-label]');
      if (label && input.dataset.shortLabel) {
        label.textContent = input.dataset.shortLabel;
      }

      this.setDirection(input.dataset.sortDirection || '');

      this.querySelectorAll('.facet-sort__option').forEach((option) => {
        option.classList.toggle('is-active', option.contains(input) && input.checked);
      });
    };

    this.addEventListener('change', this.onChange);
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.onChange);
  }

  setDirection(direction) {
    const selected = this.querySelector('.facet-sort__selected');
    const directionIcon = this.querySelector('.facet-sort__direction');
    if (!selected) return;

    selected.dataset.sortDirection = direction;

    if (!directionIcon) return;
    directionIcon.classList.toggle('hidden', !direction);
  }
}

if (!customElements.get('facet-sort')) {
  customElements.define('facet-sort', FacetSort);
}

class FacetDrawer extends HTMLElement {
  constructor() {
    super();
    this.onKeyUp = (event) => {
      if (event.code === 'Escape') this.close();
    };
    this.onOverlayClick = this.close.bind(this);
    this.onOpenClick = this.onOpenClick.bind(this);
    this.onCloseClick = this.onCloseClick.bind(this);
  }

  connectedCallback() {
    this.addEventListener('keyup', this.onKeyUp);

    this.overlay = this.querySelector('[data-facet-drawer-overlay]');
    this.overlay?.addEventListener('click', this.onOverlayClick);

    this.openButtons = document.querySelectorAll(`[aria-controls="${this.id}"]`);
    this.openButtons.forEach((button) => button.addEventListener('click', this.onOpenClick));

    this.querySelectorAll('[data-facet-drawer-close]').forEach((button) => {
      button.addEventListener('click', this.onCloseClick);
    });
  }

  disconnectedCallback() {
    this.removeEventListener('keyup', this.onKeyUp);
    this.overlay?.removeEventListener('click', this.onOverlayClick);
    this.openButtons?.forEach((button) => button.removeEventListener('click', this.onOpenClick));
    this.querySelectorAll('[data-facet-drawer-close]').forEach((button) => {
      button.removeEventListener('click', this.onCloseClick);
    });
  }

  onOpenClick(event) {
    event.preventDefault();
    this.open(event.currentTarget);
  }

  onCloseClick(event) {
    event.preventDefault();
    this.close();
  }

  open(triggeredBy) {
    if (triggeredBy) this.activeElement = triggeredBy;

    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.openButtons?.forEach((button) => button.setAttribute('aria-expanded', 'true'));

    this.addEventListener(
      'transitionend',
      () => {
        const focusElement =
          this.querySelector('.mobile-facets__apply') || this.querySelector('.drawer__inner');
        trapFocus(this.querySelector('.drawer__inner'), focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    this.openButtons?.forEach((button) => button.setAttribute('aria-expanded', 'false'));
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }
}

if (!customElements.get('facet-drawer')) {
  customElements.define('facet-drawer', FacetDrawer);
}

customElements.define('back-in-stock-modal', class BackInStockModal extends HTMLElement {
  constructor() {
    super();

    this.panel = this.querySelector('[role="dialog"]');
    this.form = this.querySelector('[data-bis-form]');
    this.successMessage = this.querySelector('[data-bis-success]');
    this.errorMessage = this.querySelector('[data-bis-error]');
    this.closeButtons = this.querySelectorAll('[data-bis-close]');
    this.variantChangeUnsubscriber = undefined;
    this.openedBy = null;

    this.onSubmit = this.onSubmit.bind(this);
    this.onKeyUp = this.onKeyUp.bind(this);
    this.onOverlayClick = this.onOverlayClick.bind(this);
  }

  connectedCallback() {
    if (this.isConnectedBound) return;
    this.isConnectedBound = true;

    this.form?.addEventListener('submit', this.onSubmit);
    this.addEventListener('keyup', this.onKeyUp);
    this.addEventListener('click', this.onOverlayClick);
    this.closeButtons.forEach((button) => button.addEventListener('click', () => this.close()));

    this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      const variant = event?.data?.variant;
      if (!variant?.id) return;
      this.dataset.variantId = variant.id;
    });
  }

  disconnectedCallback() {
    if (this.variantChangeUnsubscriber) {
      this.variantChangeUnsubscriber();
    }
  }

  open(opener) {
    const variantInput = opener?.closest('product-form')?.querySelector('input[name="id"]');
    if (variantInput?.value) {
      this.dataset.variantId = variantInput.value;
    }

    this.openedBy = opener;
    this.resetState();
    this.removeAttribute('hidden');
    this.querySelector('.back-in-stock-modal__description')?.classList.remove("hidden")
    this.querySelector('.back-in-stock-modal__title')?.classList.remove("hidden")
    document.body.classList.add('overflow-hidden');
    trapFocus(this, this.panel);
    this.querySelector('input[type="email"]')?.focus();
  }

  close() {
    this.setAttribute('hidden', '');
    document.body.classList.remove('overflow-hidden');
    removeTrapFocus(this.openedBy);
    this.openedBy = null;
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;
    this.close();
  }

  onOverlayClick(event) {
    if (event.target === this) {
      this.close();
    }
  }

  resetState() {
    this.form?.reset();
    this.form?.classList.remove('hidden');
    this.successMessage?.classList.add('hidden');
    this.errorMessage?.classList.add('hidden');
    if (this.errorMessage) {
      this.errorMessage.textContent = '';
    }
  }

  getCompanyId() {
    const fromDataset = (this.dataset.companyId || '').trim();
    if (fromDataset) return fromDataset;

    const fromSettings = (window.klaviyoBackInStock?.companyId || '').trim();
    if (fromSettings) return fromSettings;

    const script = Array.from(document.scripts).find((node) => /company_id=/.test(node.src));
    if (!script) return '';

    return new URL(script.src, window.location.origin).searchParams.get('company_id') || '';
  }

  getMarketingListId() {
    const fromDataset = (this.dataset.marketingListId || '').trim();
    if (fromDataset) return fromDataset;

    return (window.klaviyoBackInStock?.marketingListId || '').trim();
  }

  getStrings() {
    return window.klaviyoBackInStock?.strings || {};
  }

  buildVariantCompositeId(variantId) {
    return `$shopify:::$default:::${variantId}`;
  }

  getRevision() {
    return window.klaviyoBackInStock?.revision || '2026-04-15';
  }

  getKlaviyoHeaders() {
    return {
      'Content-Type': 'application/vnd.api+json',
      Accept: 'application/vnd.api+json',
      revision: this.getRevision(),
    };
  }

  async klaviyoClientRequest(url, body) {
    const response = await fetch(url, {
      method: 'POST',
      headers: this.getKlaviyoHeaders(),
      body: JSON.stringify(body),
    });

    if (response.ok) {
      return response;
    }

    const errorBody = await response.text().catch(() => '');
    throw new Error(`Klaviyo request failed (${response.status}): ${errorBody}`);
  }

  getLocale() {
    const fromDataset = (this.dataset.locale || '').trim();
    if (fromDataset) return fromDataset;

    const config = window.klaviyoBackInStock || {};
    if (config.locale) return config.locale;

    const language = (config.language || '').trim();
    const country = (config.country || '').trim();
    if (!language) return '';
    if (language.includes('-')) return language;
    if (country) return `${language}-${country}`;
    return language;
  }

  getProfileAttributes(email) {
    const attributes = { email };
    const exchangeId = document.cookie.match(/_kx=([^;]+)/);

    if (exchangeId) {
      attributes._kx = decodeURIComponent(exchangeId[1]);
    }

    return attributes;
  }

  getMarketingProfileAttributes(email) {
    const attributes = this.getProfileAttributes(email);
    const locale = this.getLocale();

    if (locale) {
      attributes.locale = locale;
    }

    return attributes;
  }

  identifyWithKlaviyoOnsite(email) {
    if (typeof window.klaviyo !== 'undefined' && typeof window.klaviyo.identify === 'function') {
      window.klaviyo.identify({ email });
      return;
    }

    if (Array.isArray(window._learnq)) {
      window._learnq.push(['identify', { $email: email }]);
    }
  }

  async createOrUpdateProfile(companyId, email) {
    await this.klaviyoClientRequest(
      `https://a.klaviyo.com/client/profiles/?company_id=${encodeURIComponent(companyId)}`,
      {
        data: {
          type: 'profile',
          attributes: this.getProfileAttributes(email),
        },
      }
    );
  }

  async onSubmit(event) {
    event.preventDefault();

    const emailInput = this.form?.querySelector('input[type="email"]');
    const marketingOptIn = this.form?.querySelector('input[name="marketing_opt_in"]')?.checked;
    const submitButton = this.form?.querySelector('[type="submit"]');
    const email = emailInput?.value.trim();
    const strings = this.getStrings();
    const companyId = (this.getCompanyId() || '').trim();
    const variantId = this.dataset.variantId;

    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      this.showError(strings.emailError);
      emailInput?.focus();
      return;
    }

    if (!companyId || !variantId) {
      this.showError(strings.error);
      return;
    }

    submitButton?.setAttribute('aria-disabled', 'true');

    try {
      const marketingListId = (this.getMarketingListId() || 'RBhrvs').trim();

      if (!marketingListId) {
        console.error('Klaviyo marketing list ID is not configured. Set it in Theme settings → Klaviyo.');
        this.showError(strings.error);
        return;
      }

      await this.createMarketingSubscription(companyId, email, marketingListId, marketingOptIn);

      this.identifyWithKlaviyoOnsite(email);
      await this.createBackInStockSubscription(companyId, email, variantId);

      this.showSuccess();
    } catch (error) {
      console.error(error);
      this.showError(strings.error);
    } finally {
      submitButton?.removeAttribute('aria-disabled');
    }
  }

  async createBackInStockSubscription(companyId, email, variantId) {
    await this.klaviyoClientRequest(
      `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${encodeURIComponent(companyId)}`,
      {
        data: {
          type: 'back-in-stock-subscription',
          attributes: {
            profile: {
              data: {
                type: 'profile',
                attributes: this.getProfileAttributes(email),
              },
            },
            channels: ['EMAIL'],
          },
          relationships: {
            variant: {
              data: {
                type: 'catalog-variant',
                id: this.buildVariantCompositeId(variantId),
              },
            },
          },
        },
      }
    );
  }

  async createMarketingSubscription(companyId, email, listId, hasMarketingConsent) {
    const profileAttributes = this.getMarketingProfileAttributes(email);

    if (hasMarketingConsent) {
      profileAttributes.subscriptions = {
        email: {
          marketing: {
            consent: 'SUBSCRIBED',
          },
        },
      };
    } else {
      profileAttributes.subscriptions = {};
    }

    await this.klaviyoClientRequest(
      `https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(companyId)}`,
      {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: 'Back in stock modal',
            profile: {
              data: {
                type: 'profile',
                attributes: profileAttributes,
              },
            },
          },
          relationships: {
            list: {
              data: {
                type: 'list',
                id: listId,
              },
            },
          },
        },
      }
    );
  }

  showSuccess() {
    this.form?.classList.add('hidden');
    this.querySelector('.back-in-stock-modal__description')?.classList.add("hidden")
    this.querySelector('.back-in-stock-modal__title')?.classList.add("hidden")
    this.errorMessage?.classList.add('hidden');
    this.successMessage?.classList.remove('hidden');
  }

  showError(message) {
    if (!this.errorMessage) return;
    this.errorMessage.textContent = message || this.getStrings().error || '';
    this.errorMessage.classList.remove('hidden');
  }
});